import React, { useState } from "react";
import GoogleMapReact from "google-map-react";
import Marker from "./components/marker";
import { google } from "../../config/environment";

interface Coordinates {
  lat: number;
  lng: number;
}

interface PolygonCoords {
  mapPolygonsLocal: Coordinates[];
  mapPolygonsExtended: Coordinates[];
}

interface MapProps {
  center?: Coordinates;
  zoom?: number;
  children?: React.ReactNode;
  defaultCenter?: Coordinates;
  defaultZoom?: number;
  addDestinationInfo: (info: any[]) => void;
  style?: React.CSSProperties;
  height?: number | string;
  width?: number | string;
}

const defaultStyle: React.CSSProperties = {
  width: "100%",
  height: 400,
};

let renderPolyLines: (locations: Coordinates[]) => void;
let renderPolygon: (coords: PolygonCoords) => void;
let renderDirectionsLine: (locations: { coordinates: number[] }[]) => void;

const fitBounds = (locations: Coordinates[], mapInternals: any) => {
  const bounds = new mapInternals.maps.LatLngBounds();
  locations.map((location) => {
    bounds.extend(new mapInternals.maps.LatLng(location.lat, location.lng));
  });
  mapInternals.map.fitBounds(bounds);
};

const Map: React.FC<MapProps> = ({
  center,
  zoom,
  children,
  defaultCenter,
  defaultZoom,
  addDestinationInfo,
  style,
  ...props
}) => {
  const [mapInternals, setMapInternals] = useState<any | undefined>(undefined);
  const [polyLine, setPolyLine] = useState<any | undefined>(undefined);
  const [polygon, setPolygon] = useState<any | undefined>(undefined);
  const [directionsLine, setDirectionsLine] = useState<any[]>([]);

  renderPolyLines = (locations: Coordinates[]) => {
    if (!mapInternals) return;
    if (polyLine) {
      polyLine.setMap(undefined);
    }

    const path = locations.map((location) => ({
      lng: location.lng,
      lat: location.lat,
    }));

    const polylineToSet = new mapInternals.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: "#800080", // Purple color (without theme)
      strokeOpacity: 0.7,
      strokeWeight: 4,
    });

    polylineToSet.setMap(mapInternals.map);
    setPolyLine(polylineToSet);
    if (locations.length > 1) {
      fitBounds(locations, mapInternals);
    }
  };

  renderDirectionsLine = (locations: { coordinates: number[] }[]) => {
    if (!mapInternals) return;

    const directionsService = new mapInternals.maps.DirectionsService();
    if (directionsLine) {
      for (let i = 0; i < directionsLine.length; i++) {
        directionsLine[i].setMap(null);
      }
    }
    const responses: any[] = [];
    locations.forEach((location, idx) => {
      if (idx + 1 !== locations.length) {
        const directionsRenderer = new mapInternals.maps.DirectionsRenderer();
        directionsService
          .route({
            origin: {
              lng: location.coordinates[0],
              lat: location.coordinates[1],
            },
            destination: {
              lng: locations[idx + 1].coordinates[0],
              lat: locations[idx + 1].coordinates[1],
            },
            travelMode: mapInternals.maps.TravelMode.DRIVING,
          })
          .then((response) => {
            directionsRenderer.setDirections(response);
            directionsRenderer.setMap(mapInternals.map);
            responses.push(response);

            setDirectionsLine([...directionsLine, directionsRenderer]);
          })
          .catch((e) => console.log("Directions request failed due to " + e));
      }
    });
    addDestinationInfo(responses);
  };

  renderPolygon = (coords: PolygonCoords) => {
    if (!mapInternals) return;
    if (polygon) {
      polygon?.local?.setMap(undefined);
      polygon?.extended?.setMap(undefined);
    }
    const local = new mapInternals.maps.Polygon({
      paths: coords.mapPolygonsLocal,
      strokeColor: "#800080", // Purple color (without theme)
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#800080", // Purple color (without theme)
      fillOpacity: 0.5,
    });
    local.setMap(mapInternals.map);

    const extended = new mapInternals.maps.Polygon({
      paths: coords.mapPolygonsExtended,
      strokeColor: "#800080", // Purple color (without theme)
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#ff00ff", // Secondary color (without theme)
      fillOpacity: 0.5,
    });

    extended.setMap(mapInternals.map);
    setPolygon({ local,
      extended });
  };

  return (
    <div style={{ ...defaultStyle,
      ...style }} {...props}>
      <GoogleMapReact
        yesIWantToUseGoogleMapApiInternals
        bootstrapURLKeys={{ key: google.apiKey,
          libraries: "places" }}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        layerTypes={["TrafficLayer", "TransitLayer"]}
        center={center}
        zoom={zoom}
        onGoogleApiLoaded={({ map, maps }) => {
          if (!mapInternals) {
            setMapInternals({ map,
              maps });
          }
        }}
      >
        {children}
      </GoogleMapReact>
    </div>
  );
};

const defaultZoom = 13;
const defaultCenter = {
  lat: 25.6827726, // Default Lat for Tec de Monterrey
  lng: -100.31142344, // Default Lng for Tec de Monterrey
};

Map.defaultProps = {
  height: 400,
  width: "100%",
  zoom: defaultZoom,
  center: defaultCenter,
  defaultCenter,
  children: undefined,
  defaultZoom,
  style: {},
};

export { renderPolyLines, Marker, renderPolygon, renderDirectionsLine };
export default Map;
